import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { getPortfolioNews } from 'src/app/home/adapters'
import { mapPortfolioNewsItem } from 'src/app/home/pages/portfolio-news/domain/use-cases/map-portfolio-news-item'
import type { IPortfolioNewsArticleEntity } from 'src/app/home/pages/portfolio-news/types'
import { portfolioNewsKeys } from 'src/app/home/queries/keys'

export interface IPortfolioNewsQueryResult {
  items: IPortfolioNewsArticleEntity[]
  total: number
  page: number
  pageSize: number
}

export interface IUsePortfolioNewsQueryParams {
  page: number
  pageSize: number
  search: string
  company: string
  enabled: boolean
  /** When true, the global query error toast is suppressed (caller handles UX). */
  silentErrorToast?: boolean
}

export const usePortfolioNewsQuery = (
  params: IUsePortfolioNewsQueryParams,
): UseQueryResult<IPortfolioNewsQueryResult, Error> => {
  return useQuery({
    queryKey: portfolioNewsKeys.list({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      company: params.company,
    }),
    ...(params.silentErrorToast ? { meta: { silent: true } } : {}),
    queryFn: async (): Promise<IPortfolioNewsQueryResult> => {
      const response = await getPortfolioNews({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search || undefined,
        company: params.company || undefined,
      })
      return {
        items: response.data.map(mapPortfolioNewsItem),
        total: response.total,
        page: response.page,
        pageSize: response.page_size,
      }
    },
    enabled: params.enabled,
    placeholderData: keepPreviousData,
  })
}
